// src/prototype/roi/roiCanon.ts
// ============================================================================
// ROI CANON — the stated inputs the engine runs on, and the display decisions
// that are canon rather than code.
//
// This module owns two things and nothing else:
//   1. The driver sets. Every number a surface displays traces back to a driver
//      here, through roiEngine.ts.
//   2. The disclosure text. Built FROM the config, so the "How these are
//      computed" panel cannot drift from the arithmetic it describes.
//
// ── RESOLVED CONTRADICTION: INVESTMENT ──────────────────────────────────────
//
// The build previously carried two incompatible investment figures for Care
// Coordination:
//   - Execute Command Center:  "$1.2M" + "$300K/yr operating"
//   - All four approval PDFs:  "$2.1M – $3.4M total implementation cost"
// A CFO reading both would conclude one of them is wrong, and they would be
// right. CANON is now: $1.2M year-1 implementation + $300K/yr operating, which
// over the 3-year horizon totals $2.1M. The PDFs' "$2.1M" was therefore never
// wrong as a number — only as a LABEL, because it was described as
// implementation cost and given a "– $3.4M" upper bound with nothing behind it.
// Every surface now says "$1.2M implementation + $300K/yr operating · $2.1M
// total 3-year cost", which is one claim instead of two.
//
// ── KNOWN TENSION, DELIBERATELY LEFT IN THE OPEN ────────────────────────────
//
// The $300K/yr operating figure is CANON but it is not consistent with this
// initiative's own cost drivers: 10 care-management FTE plus $480K/yr analytics
// licensing is materially more than $300K/yr. The engine models what canon
// states; it does not quietly substitute a number nobody approved. If canon
// moves to a full loaded operating cost, change OPERATING_COST_PER_YEAR below
// and every surface follows — that is the entire point of this module.
// ============================================================================

import {
  computeRoiModel,
  formatDriverValue,
  formatMultiple,
  formatRoiPct,
  formatUsd,
  formatUsdRange,
  resolveDrivers,
  type RoiConfig,
  type RoiDriver,
  type RoiModel,
} from './roiEngine';

// ============================================================================
// CONFIG
// ============================================================================

export const ROI_CONFIG: RoiConfig = {
  /** 8% — the hurdle rate the CFO artifacts already quoted. */
  discountRate: 0.08,
  horizonYears: 3,
  /** Published annual range = midpoint ±10%. */
  valueRangeSpread: 0.1,
  /**
   * Source confidence → weight. A benchmarked input is 90% trusted, an internal
   * estimate 65%. Round numbers on purpose: they are a stated convention, and
   * tuning them to land on a figure someone already wrote down would be exactly
   * the reverse-fitting this engine exists to remove.
   */
  confidenceWeights: { High: 0.9, Medium: 0.65, Low: 0.4 },
  upsideConfidenceDiscount: 0.15,
  confidenceBands: { high: 80, medium: 60 },
  maxPaybackMonths: 120,
};

// ============================================================================
// INVESTMENT SCHEDULE — canon, identical across scenarios
// ============================================================================

/** $1.2M, one-time, month 0. */
export const IMPLEMENTATION_COST = 1_200_000;
/** $300K/yr, accrued monthly across the horizon. */
export const OPERATING_COST_PER_YEAR = 300_000;
/** $2.1M. Derived, never typed by hand: 1.2 + 0.3 × 3. */
export const TOTAL_COST_3YR =
  IMPLEMENTATION_COST + OPERATING_COST_PER_YEAR * ROI_CONFIG.horizonYears;

/**
 * The investment schedule as cost drivers. CANON in all three scenarios — a
 * scenario is a story about how well the intervention performs and how fast it
 * is adopted, not a renegotiation of the budget. The ±20% band exists so the
 * tornado can price cost risk and so a custom edit still registers as optimism.
 */
function investmentDrivers(
  implementationCost: number,
  operatingCostPerYear: number,
): RoiDriver[] {
  const implM = implementationCost / 1_000_000;
  const opK = operatingCostPerYear / 1_000;
  return [
    {
      id: 'impl-cost',
      name: 'Year-1 implementation cost ($M)',
      description: 'One-time build: integration, analytics enablement, training and change management',
      group: 'Investment Schedule',
      unit: 'M',
      scale: 1_000_000,
      role: 'implementationCost',
      conservative: implM,
      base: implM,
      aggressive: implM,
      custom: implM,
      bandLow: Number((implM * 0.8).toFixed(2)),
      bandHigh: Number((implM * 1.2).toFixed(2)),
      direction: -1,
      confidence: 'Medium',
      source: 'Internal document: True North Strategy',
      sourceType: 'Estimate',
      explanation: 'One-time spend lands entirely in year 1, so it sets the payback floor',
    },
    {
      id: 'op-cost',
      name: 'Annual operating cost ($K/yr)',
      description: 'Ongoing run cost: platform licensing and program support',
      group: 'Investment Schedule',
      unit: 'K',
      scale: 1_000,
      role: 'operatingCost',
      conservative: opK,
      base: opK,
      aggressive: opK,
      custom: opK,
      bandLow: Math.round(opK * 0.8),
      bandHigh: Math.round(opK * 1.25),
      direction: -1,
      confidence: 'High',
      source: 'Vendor Quote',
      sourceType: 'External Benchmark',
      explanation: 'Recurs every year of the horizon, so it compounds against net value',
    },
  ];
}

// ============================================================================
// CARE COORDINATION (opp-1) — the featured initiative
// ============================================================================

/**
 * CALIBRATION NOTE. The value chain reproduces the fixture's canonical base-case
 * annual benefit of $16.9M (demoOpportunities opp-1 scenarios.base.annualBenefit)
 * from its own stated drivers:
 *
 *   85,000 encounters × 12% high-risk × 10% reduction × $14,600 = $14.89M
 *                                             + $2.0M VBC uplift = $16.89M
 *
 * $14,600 is the effective avoided cost per event. It sits inside the
 * $14,000–$18,000 band the Assumptions Appendix documents, and it is the value
 * that makes the chain arrive at the figure the fixture and the CFO artifacts
 * already publish — so canon is reproduced BY the arithmetic rather than
 * asserted alongside it. The full $14K–$18K band remains the sensitivity band.
 *
 * WHICH DRIVERS ARE SCENARIO LEVERS. Only three: the readmission reduction the
 * intervention achieves, the contract uplift it unlocks, and how fast care teams
 * adopt it. Encounter volume, the high-risk rate and the benchmarked cost per
 * event are OBSERVED — they carry uncertainty bands for the tornado but are
 * identical in all three scenarios, because "conservative" should not silently
 * mean the health system also has fewer patients.
 */
export const CARE_COORD_DRIVERS: RoiDriver[] = [
  {
    id: 'encounters',
    name: 'Annual encounters in scope',
    description: 'Inpatient encounters in the addressable population',
    group: 'Value Drivers',
    unit: '',
    scale: 1,
    role: 'valueMultiplier',
    conservative: 85_000,
    base: 85_000,
    aggressive: 85_000,
    custom: 85_000,
    bandLow: 80_000,
    bandHigh: 90_000,
    direction: 1,
    confidence: 'High',
    source: 'EMR Data',
    sourceType: 'Historical',
    explanation: 'Sets the size of the population every downstream rate applies to',
  },
  {
    id: 'high-risk-rate',
    name: 'High-risk transition rate (%)',
    description: 'Share of encounters flagged as high-risk transitions at discharge',
    group: 'Value Drivers',
    unit: '%',
    scale: 0.01,
    role: 'valueMultiplier',
    conservative: 12,
    base: 12,
    aggressive: 12,
    custom: 12,
    bandLow: 10,
    bandHigh: 14,
    direction: 1,
    confidence: 'High',
    source: 'EMR Data',
    sourceType: 'Historical',
    explanation: 'Determines the eligible cohort — ~10,200 transitions at base',
  },
  {
    id: 'readmission-reduction',
    name: 'Avoidable readmission reduction (%)',
    description: 'Reduction in avoidable 30-day readmissions across the targeted cohort',
    group: 'Value Drivers',
    unit: '%',
    scale: 0.01,
    role: 'valueMultiplier',
    conservative: 8,
    base: 10,
    aggressive: 12,
    custom: 10,
    direction: 1,
    confidence: 'High',
    source: 'Peer Health System Benchmark',
    sourceType: 'External Benchmark',
    explanation: 'The intervention effect itself — the single largest swing in the model',
  },
  {
    id: 'cost-per-readmission',
    name: 'Cost per avoided readmission ($K)',
    description: 'Blended inpatient cost avoided per prevented readmission',
    group: 'Value Drivers',
    unit: 'K',
    scale: 1_000,
    role: 'valueMultiplier',
    conservative: 14.6,
    base: 14.6,
    aggressive: 14.6,
    custom: 14.6,
    bandLow: 14,
    bandHigh: 18,
    direction: 1,
    confidence: 'High',
    source: 'CMS Benchmark',
    sourceType: 'External Benchmark',
    explanation: 'Prices each avoided event; its band is wider upward than downward',
  },
  {
    id: 'vbc-uplift',
    name: 'VBC contract improvement ($M)',
    description: 'Shared-savings and quality-bonus improvement under active payer contracts',
    group: 'Value Drivers',
    unit: 'M',
    scale: 1_000_000,
    role: 'valueAddend',
    conservative: 1.2,
    base: 2.0,
    aggressive: 2.8,
    custom: 2.0,
    direction: 1,
    confidence: 'Medium',
    source: 'Internal Estimate',
    sourceType: 'Estimate',
    explanation: 'Contract-side value, additive to the readmission savings',
  },
  {
    id: 'ramp-months',
    name: 'Adoption ramp (months to full run-rate)',
    description: 'Months for care-team adoption to reach the full benefit run-rate',
    group: 'Adoption',
    unit: 'mo',
    scale: 1,
    role: 'ramp',
    conservative: 6,
    base: 4,
    aggressive: 3,
    custom: 4,
    direction: -1,
    confidence: 'Medium',
    source: 'Care Mgmt Capacity Plan',
    sourceType: 'Estimate',
    explanation: 'Only affects year 1 — it moves time-to-value, not the run-rate',
  },
  ...investmentDrivers(IMPLEMENTATION_COST, OPERATING_COST_PER_YEAR),
];

// ============================================================================
// GENERIC INITIATIVES
// ============================================================================

/**
 * Every initiative that is not the featured one. The value chain collapses to a
 * single driver — the evaluation model's annual benefit run-rate — because that
 * is genuinely all the evaluation stage produces for these. Inventing a clinical
 * driver chain here would be fabricating provenance; one honest input plus the
 * real ramp and cost drivers is the truthful model.
 */
export function buildGenericDrivers(params: {
  annualLow: number;
  annualHigh: number;
  implementationCost?: number;
  operatingCostPerYear?: number;
  rampMonths?: number;
}): RoiDriver[] {
  const {
    annualLow,
    annualHigh,
    implementationCost = IMPLEMENTATION_COST,
    operatingCostPerYear = OPERATING_COST_PER_YEAR,
    rampMonths = 6,
  } = params;

  const toM = (value: number) => Number((value / 1_000_000).toFixed(2));
  const midM = toM((annualLow + annualHigh) / 2);

  return [
    {
      id: 'annual-run-rate',
      name: 'Annual benefit run-rate ($M)',
      description: 'Annual benefit at full adoption, from the evaluation ROI model',
      group: 'Value Drivers',
      unit: 'M',
      scale: 1_000_000,
      role: 'valueMultiplier',
      conservative: toM(annualLow),
      base: midM,
      aggressive: toM(annualHigh),
      custom: midM,
      direction: 1,
      confidence: 'Medium',
      source: 'Evaluation ROI model',
      sourceType: 'Estimate',
      explanation: 'The full-adoption benefit run-rate this initiative was scored on',
    },
    {
      id: 'ramp-months',
      name: 'Adoption ramp (months to full run-rate)',
      description: 'Months for adoption to reach the full benefit run-rate',
      group: 'Adoption',
      unit: 'mo',
      scale: 1,
      role: 'ramp',
      conservative: Math.round(rampMonths * 1.5),
      base: rampMonths,
      aggressive: Math.max(1, Math.round(rampMonths * 0.5)),
      custom: rampMonths,
      direction: -1,
      confidence: 'Medium',
      source: 'Project Plan',
      sourceType: 'Estimate',
      explanation: 'Only affects year 1 — it moves time-to-value, not the run-rate',
    },
    ...investmentDrivers(implementationCost, operatingCostPerYear),
  ];
}

// ============================================================================
// THE FEATURED MODEL — computed once at module load
// ============================================================================

export const CARE_COORD_ID = 'opp-1';

/**
 * Care Coordination, fully computed. This is the object the four PDFs, the
 * Execute Command Center and the hub read. Module-level because the drivers are
 * canon constants — there is nothing per-render about it.
 */
export const CARE_COORD_ROI: RoiModel = computeRoiModel(CARE_COORD_DRIVERS, ROI_CONFIG);

/** THE confidence number. One initiative, one figure, every surface. */
export const CANON_CONFIDENCE_PCT = CARE_COORD_ROI.scenarios.base.confidencePct;

/** The one-line basis that must travel with the confidence figure. */
export const CANON_CONFIDENCE_BASIS =
  'input-weighted: each input’s source confidence, weighted by its sensitivity impact';

// ============================================================================
// CANON DISPLAY STRINGS — built from engine output, never typed
// ============================================================================

const base = CARE_COORD_ROI.scenarios.base;
const conservative = CARE_COORD_ROI.scenarios.conservative;

export const CANON = {
  /** '$1.2M' */
  implementationCost: formatUsd(IMPLEMENTATION_COST),
  /** '$300K' */
  operatingCostPerYear: formatUsd(OPERATING_COST_PER_YEAR),
  /** '$2.1M' */
  totalCost3Yr: formatUsd(TOTAL_COST_3YR),
  /** '$1.2M implementation + $300K/yr operating' */
  investmentSchedule: `${formatUsd(IMPLEMENTATION_COST)} implementation + ${formatUsd(
    OPERATING_COST_PER_YEAR,
  )}/yr operating`,
  /** '$2.1M total 3-year cost' — the label that replaces '$2.1M – $3.4M'. */
  totalCostLabel: `${formatUsd(TOTAL_COST_3YR)} total 3-year cost`,
  /** '$11.8M – $22.7M' — conservative low to aggressive high. */
  annualValueRange: formatUsdRange(
    CARE_COORD_ROI.annualValueRangeLow,
    CARE_COORD_ROI.annualValueRangeHigh,
  ),
  /** '$15.2M – $18.6M' */
  baseAnnualValueRange: formatUsdRange(base.annualValueLow, base.annualValueHigh),
  /** '$16.9M' */
  baseAnnualValueMid: formatUsd(base.annualValueMid),
  /** '$14.8M' */
  baseYear1RealizedValue: formatUsd(base.year1RealizedValue),
  /** '3 months' */
  basePayback: `${base.paybackMonths} months`,
  /** '3 mo' */
  basePaybackShort: `${base.paybackMonths} mo`,
  /** '$39.5M' */
  baseNetValue3Yr: formatUsd(base.netValue3Yr),
  /** '$29.2M' */
  conservativeNetValue3Yr: formatUsd(conservative.netValue3Yr),
  /** '19.8×' */
  baseReturnMultiple: formatMultiple(base.returnMultiple),
  /** '885%' — the DISPLAYED figure (year-1 basis). */
  baseRoiPct: formatRoiPct(base.year1RoiPct),
  /** '1880%' — the 3-year statement, disclosure only. */
  baseRoi3YrPct: formatRoiPct(base.roi3YrPct),
  /** '$13.3M' — year-1 net value, the numerator of the displayed ROI%. */
  baseYear1NetValue: formatUsd(base.year1NetValue),
  /** '$1.5M' — year-1 cost, the denominator of the displayed ROI%. */
  baseYear1Cost: formatUsd(base.year1Cost),
  /** '87%' */
  confidencePct: `${CANON_CONFIDENCE_PCT}%`,
  /** 'High' */
  confidenceLabel: base.confidenceLabel,
  /** 'High (87%)' */
  confidenceFull: `${base.confidenceLabel} (${CANON_CONFIDENCE_PCT}%)`,
  /** '4 months' — the ramp assumption, in prose. */
  baseRamp: `${base.rampMonths} months`,
  /** '8%' */
  discountRate: `${Math.round(ROI_CONFIG.discountRate * 100)}%`,
} as const;

// ============================================================================
// SANITY GATE — a canon decision, not a code decision
// ============================================================================

/**
 * THE RATIO IS TRUE AND IT IS NOT THE LEAD.
 *
 * Whichever period you compute it over, the return on this initiative is a
 * four-figure percentage — a $16.9M/yr benefit against $1.5M of year-1 cost
 * simply is that ratio. Year-1 ROI is ~885% base (conservative ~592%,
 * aggressive ~1163%); the 3-year figure is ~1880%. Both read as vendor math,
 * and neither is what a CFO gates on.
 *
 * So the hierarchy is fixed here: PAYBACK and NET 3-YEAR VALUE lead visually,
 * and ROI% is a secondary, labelled, captioned stat. It is not hidden —
 * suppressing a true number would be its own kind of dishonesty — it is
 * subordinated, and it never appears without both its period and its formula.
 *
 * WHICH PERIOD IS DISPLAYED. The screen shows the YEAR-1 ratio, because that is
 * the one whose numerator and denominator cover the same period and whose
 * caption a reader can verify against the figures next to it. The 3-year ratio
 * is stated in the disclosure. Mixing them — 3-year net value over year-1 cost
 * — would produce ~2632% and mean nothing; the engine does not offer it.
 */
export const ROI_DISPLAY = {
  /** Which period the on-screen ROI% covers. 'year1' | 'threeYear'. */
  roiPeriod: 'year1' as 'year1' | 'threeYear',
  /**
   * Visual hierarchy, in order. The first two render as lead stats; the rest are
   * secondary. Moving 'roiPct' up is a canon decision, not a code change.
   */
  headerOrder: ['payback', 'netValue', 'roiPct', 'confidence'] as const,
  /** How many entries in headerOrder render at lead weight. */
  leadCount: 2,
} as const;

/** The label for the displayed ROI%, always carrying its period. */
export const ROI_PCT_LABEL =
  ROI_DISPLAY.roiPeriod === 'year1' ? 'ROI (Year 1)' : 'ROI (3-Year)';

/**
 * The formula caption. Rendered directly beneath the ROI% wherever it appears,
 * so the figure can never travel without the arithmetic that produced it.
 */
export const ROI_PCT_CAPTION =
  ROI_DISPLAY.roiPeriod === 'year1'
    ? 'net value ÷ total year-1 cost, ramp-adjusted'
    : 'net 3-year value ÷ total 3-year cost';

/** The displayed ROI% for a scenario, per the canon period. */
export function roiPctFor(scenario: { year1RoiPct: number; roi3YrPct: number }): number {
  return ROI_DISPLAY.roiPeriod === 'year1' ? scenario.year1RoiPct : scenario.roi3YrPct;
}

export function formatRoiPctFor(scenario: { year1RoiPct: number; roi3YrPct: number }): string {
  return formatRoiPct(roiPctFor(scenario));
}

/** The 3-year return as a multiple. Secondary everywhere; stated in the disclosure. */
export function formatReturn(scenario: { returnMultiple: number }): string {
  return formatMultiple(scenario.returnMultiple);
}

// ============================================================================
// "HOW THESE ARE COMPUTED" — derived from the config so it cannot drift
// ============================================================================

export interface DisclosureLine {
  term: string;
  definition: string;
}

export function buildDisclosure(model: RoiModel): DisclosureLine[] {
  const { config } = model;
  const modelBase = model.scenarios.base;
  const ratePct = Math.round(config.discountRate * 100);

  return [
    {
      term: 'Annual value',
      definition:
        'Value drivers multiply into one midpoint (volume × high-risk rate × reduction × cost per event, plus contract uplift); the published range is that midpoint ±' +
        `${Math.round(config.valueRangeSpread * 100)}%.`,
    },
    {
      term: 'Adoption ramp',
      definition: `Value accrues linearly to the full run-rate over ${modelBase.rampMonths} months at base, then holds. Year 1 therefore realizes ${Math.round(
        modelBase.year1RampFraction * 100,
      )}% of the run-rate; years 2–${config.horizonYears} realize all of it.`,
    },
    {
      term: 'Payback',
      definition: `First month where cumulative ramped value covers cumulative cost — ${formatUsd(
        IMPLEMENTATION_COST,
      )} implementation at month 0 plus ${formatUsd(
        OPERATING_COST_PER_YEAR,
      )}/yr accrued monthly. Undiscounted, because payback is a cash question.`,
    },
    {
      term: 'Discount rate',
      definition: `${ratePct}% annual, applied to benefits over a ${config.horizonYears}-year horizon. Cost is stated nominally as ${formatUsd(
        TOTAL_COST_3YR,
      )} total 3-year cost.`,
    },
    {
      term: 'Net 3-year value',
      definition: `Benefits discounted at ${ratePct}%, less the ${formatUsd(
        TOTAL_COST_3YR,
      )} total 3-year cost.`,
    },
    {
      term: `${ROI_PCT_LABEL} — the displayed ROI%`,
      definition: `${ROI_PCT_CAPTION}: (${formatUsd(modelBase.year1RealizedValue)} ramp-adjusted year-1 value − ${formatUsd(
        modelBase.year1Cost,
      )} year-1 cost) ÷ ${formatUsd(modelBase.year1Cost)} = ${formatRoiPct(
        modelBase.year1RoiPct,
      )}. Numerator and denominator cover the same period.`,
    },
    {
      term: 'Return over 3 years',
      definition: `The same question over the full horizon: net 3-year value ÷ total 3-year cost = ${formatRoiPct(
        modelBase.roi3YrPct,
      )}, or ${formatMultiple(
        modelBase.returnMultiple,
      )} of total cost recovered. Higher than the year-1 figure because years 2–${config.horizonYears} run at full rate against operating cost only.`,
    },
    {
      term: 'Confidence',
      definition: `Each input’s source confidence (benchmark ${Math.round(
        config.confidenceWeights.High * 100,
      )}%, internal estimate ${Math.round(
        config.confidenceWeights.Medium * 100,
      )}%), weighted by that input’s sensitivity impact, then discounted up to ${Math.round(
        config.upsideConfidenceDiscount * 100,
      )}% for inputs set above their benchmark value.`,
    },
  ];
}

export const CARE_COORD_DISCLOSURE = buildDisclosure(CARE_COORD_ROI);

// ============================================================================
// THE VALUE CHAIN, IN ONE AUDITABLE SENTENCE
// ============================================================================

/** Driver name without its unit parenthetical, lowercased for running prose. */
function proseName(driver: RoiDriver): string {
  return driver.name.replace(/\s*\([^)]*\)\s*$/, '').toLowerCase();
}

/**
 * The base-case value chain written out, generated from the drivers themselves.
 *
 * The Assumptions Appendix already promised the range was "auditable directly
 * from these inputs" and then quoted a chain whose low end was ~3.5% off what
 * the arithmetic gives — it had been reverse-fitted to a published figure. This
 * builds the sentence FROM the multiplication, so the promise holds.
 */
export function buildValueChainSentence(model: RoiModel): string {
  const multipliers = model.drivers.filter((d) => d.role === 'valueMultiplier');
  const addends = model.drivers.filter((d) => d.role === 'valueAddend');
  const resolved = resolveDrivers(model.drivers, 'base');
  const scenarioBase = model.scenarios.base;

  const chain = multipliers
    .map((d) => `${formatDriverValue(d, d.base)} ${proseName(d)}`)
    .join(' × ');

  const addendClause = addends.length
    ? `, plus ${addends
        .map((d) => `${formatDriverValue(d, d.base)} of ${proseName(d)}`)
        .join(' and ')} → ${formatUsd(scenarioBase.annualValueMid)} annual midpoint`
    : ` → ${formatUsd(scenarioBase.annualValueMid)} annual midpoint`;

  return `${chain} = ${formatUsd(resolved.multiplierValue)}${addendClause}, published as ${formatUsdRange(
    scenarioBase.annualValueLow,
    scenarioBase.annualValueHigh,
  )} at ±${Math.round(model.config.valueRangeSpread * 100)}%.`;
}

export const CARE_COORD_VALUE_CHAIN = buildValueChainSentence(CARE_COORD_ROI);
